'use client'

import { useState, useTransition } from 'react'
import { submitDemoRequest, type DemoRequestData } from '@/lib/actions/demo-request'

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
]

type Props = {
  isOpen: boolean
  onClose: () => void
}

export function DemoRequestModal({ isOpen, onClose }: Props) {
  const [isPending, startTransition] = useTransition()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [formData, setFormData] = useState<DemoRequestData>({
    businessName: '',
    contactName: '',
    email: '',
    phone: '',
    numBays: undefined,
    city: '',
    state: '',
    currentSystem: '',
    message: '',
  })

  if (!isOpen) return null

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.businessName || !formData.contactName || !formData.email) {
      setError('Please fill in all required fields.')
      return
    }

    startTransition(async () => {
      const result = await submitDemoRequest(formData)
      if (result.success) {
        setSuccess(true)
      } else {
        setError(result.error || 'Something went wrong. Please try again.')
      }
    })
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'numBays' ? (value ? parseInt(value, 10) : undefined) : value,
    }))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[#001716] rounded-2xl border border-[#E4FFE7]/[0.1] shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 transition-colors"
        >
          <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {success ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#71ED7F]/20 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-[#71ED7F]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-white mb-3">Request Received</h3>
            <p className="text-white/50 mb-6">
              We'll reach out within 24 hours to schedule your demo. Keep an eye on your inbox.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 rounded-full bg-[#71ED7F] text-[#001716] font-semibold text-sm hover:brightness-105 transition-all"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="p-6 pb-0">
              <h3 className="text-2xl font-semibold text-white mb-2">Book a Demo</h3>
              <p className="text-white/50 text-sm">
                See how Ordo can transform your facility. We'll show you exactly how your club would look.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Business Name */}
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5">
                  Facility Name <span className="text-[#71ED7F]">*</span>
                </label>
                <input
                  type="text"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  placeholder="e.g., Boulevard Golf Club"
                  className="w-full px-4 py-2.5 rounded-lg bg-[#002C2B] border border-[#E4FFE7]/[0.1] text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#71ED7F]/50 transition-colors"
                  required
                />
              </div>

              {/* Contact Name */}
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5">
                  Your Name <span className="text-[#71ED7F]">*</span>
                </label>
                <input
                  type="text"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  placeholder="Full name"
                  className="w-full px-4 py-2.5 rounded-lg bg-[#002C2B] border border-[#E4FFE7]/[0.1] text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#71ED7F]/50 transition-colors"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5">
                  Email <span className="text-[#71ED7F]">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@yourclub.com"
                  className="w-full px-4 py-2.5 rounded-lg bg-[#002C2B] border border-[#E4FFE7]/[0.1] text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#71ED7F]/50 transition-colors"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5">
                  Phone (optional)
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="w-full px-4 py-2.5 rounded-lg bg-[#002C2B] border border-[#E4FFE7]/[0.1] text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#71ED7F]/50 transition-colors"
                />
              </div>

              {/* City & State - Two columns */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-white/60 mb-1.5">
                    City (optional)
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Nashville"
                    className="w-full px-4 py-2.5 rounded-lg bg-[#002C2B] border border-[#E4FFE7]/[0.1] text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#71ED7F]/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-white/60 mb-1.5">
                    State (optional)
                  </label>
                  <select
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 rounded-lg bg-[#002C2B] border border-[#E4FFE7]/[0.1] text-white text-sm focus:outline-none focus:border-[#71ED7F]/50 transition-colors"
                  >
                    <option value="">Select...</option>
                    {US_STATES.map((state) => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Number of Bays */}
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5">
                  Number of Bays (optional)
                </label>
                <input
                  type="number"
                  name="numBays"
                  value={formData.numBays || ''}
                  onChange={handleChange}
                  placeholder="e.g., 6"
                  min="1"
                  max="50"
                  className="w-full px-4 py-2.5 rounded-lg bg-[#002C2B] border border-[#E4FFE7]/[0.1] text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#71ED7F]/50 transition-colors"
                />
              </div>

              {/* Current System */}
              <div>
                <label className="block text-xs font-medium text-white/60 mb-1.5">
                  Current booking system (optional)
                </label>
                <input
                  type="text"
                  name="currentSystem"
                  value={formData.currentSystem}
                  onChange={handleChange}
                  placeholder="e.g., Spreadsheets, Birrdi, Calendly..."
                  className="w-full px-4 py-2.5 rounded-lg bg-[#002C2B] border border-[#E4FFE7]/[0.1] text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-[#71ED7F]/50 transition-colors"
                />
              </div>

              {error && (
                <p className="text-red-400 text-sm">{error}</p>
              )}

              <button
                type="submit"
                disabled={isPending}
                className="w-full py-3 rounded-full bg-[#71ED7F] text-[#001716] font-semibold text-sm hover:brightness-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPending ? 'Submitting...' : 'Request Demo'}
              </button>

              <p className="text-center text-white/30 text-xs">
                No commitment. We'll reach out within 24 hours.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
